import './POSSortCategories.scss'

import React, { useEffect, useMemo, useState } from 'react'

import { Modal, Row, Col } from 'react-bootstrap'
import { Button, Paragraph, Select } from 'src/components'
import { faSave } from '@fortawesome/free-solid-svg-icons'

import { useSelector } from 'react-redux'
import { selectAllCategorizations } from 'src/selectors/categorizations.selector'
import {
  buildCategoryTree,
  flattenCategoryTree,
} from 'src/content/Restaurant/POSCategories/POSCategoriesFunctions'
import { POSManualSortCategories } from 'src/content/Restaurant/POSCategories/POSManualSortCategories'

interface POSSortCategories {
  show: boolean
  categories: ICategory[]
  onChange: (categories: ICategory[]) => void
}

// Tipos de ordenamiento
type SortType = 'creation' | 'alphabetical' | 'custom'
const sortTypes: ISelectString[] = [
  { value: 'creation', label: 'Ordenar por creación (ID)' },
  { value: 'alphabetical', label: 'Ordenar alfabéticamente' },
  { value: 'custom', label: 'Ordenar manualmente' },
]

const sortChildrenDefault: ICategoryChildrenToSort = {
  category: { id: -1, name: '', children: [] },
  children: [],
}

export const POSSortCategories = ({ show, categories, onChange }: POSSortCategories) => {
  const categorization = useSelector(selectAllCategorizations)

  const [sortCategories, setSortCategories] = useState<ICategory[]>([])
  const [sortType, setSortType] = useState<SortType>('creation')
  const [posCategories, setPosCategories] = useState<ICategorization[]>([])
  const [childrenToSort, setChildrenToSort] =
    useState<ICategoryChildrenToSort>(sortChildrenDefault)

  const isChildrenToSort = childrenToSort.category.id !== -1

  const categoryTree: ICategoryNode[] = useMemo(() => {
    return buildCategoryTree(sortCategories, posCategories)
  }, [sortCategories, posCategories])

  // Set category parent id
  useEffect(() => {
    if (!show) return
    const posCategories: ICategorization[] =
      categorization?.children?.find(c => c.id === -2)?.children ?? []
    setPosCategories(posCategories)
    setSortCategories(categories)
  }, [show])

  // Ordenar por creación (ID)
  const sortByCreation = () => {
    const sorted = [...categories].sort((a, b) => {
      const idA = typeof a.id === 'string' ? parseInt(a.id) : (a.id as number)
      const idB = typeof b.id === 'string' ? parseInt(b.id) : (b.id as number)
      return idA - idB
    })
    setSortCategories(sorted)
  }

  // Ordenar alfabéticamente por nombre
  const sortByAlphabetical = () => {
    const sorted = [...categories].sort((a, b) => {
      return a.name.localeCompare(b.name)
    })
    setSortCategories(sorted)
  }

  const updateManuallySortedCategories = (categoriesSorted: ICategoryNode[]) => {
    const originalCategories = [...sortCategories]
    const updatedCategories = originalCategories.filter(
      cat => !categoriesSorted.some(sortedCat => sortedCat.id === cat.id),
    )

    setSortCategories(() => [...categoriesSorted, ...updatedCategories])
  }

  return (
    <div>
      <Modal
        show={show && !isChildrenToSort}
        centered
        onHide={() => onChange(categories)}>
        <Modal.Header closeButton>
          <Modal.Title>Ordenar categorías</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Col xl={12}>
            <Paragraph>
              Seleccione el tipo de ordenamiento para sus categorías o arrástrelas para
              ordenarlas manualmente.
            </Paragraph>
          </Col>

          <Col xl={12}>
            <Select
              label={'Tipo de ordenamiento'}
              options={sortTypes}
              value={sortTypes.find(type => type.value === sortType)}
              onChange={(selected: ISelectString) => {
                const selectedType = selected.value as SortType
                setSortType(selectedType)
                if (selectedType === 'creation') sortByCreation()
                else if (selectedType === 'alphabetical') sortByAlphabetical()
              }}
            />
          </Col>

          <Col xl={12} className="mt-4">
            <POSManualSortCategories
              categories={categoryTree}
              setCategories={updateManuallySortedCategories}
              sortType={sortType}
              onSortChildren={setChildrenToSort}
            />
          </Col>
        </Modal.Body>
        <Modal.Footer>
          <Row className={'container-buttons'}>
            <Button
              icon={faSave}
              onClick={() => onChange(flattenCategoryTree(categoryTree))}>
              Guardar
            </Button>
          </Row>
        </Modal.Footer>
      </Modal>

      <Modal show={isChildrenToSort} centered>
        <Modal.Header>
          <Modal.Title>{childrenToSort.category.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <POSManualSortCategories
            categories={childrenToSort.children}
            setCategories={children => setChildrenToSort({ ...childrenToSort, children })}
            sortType={sortType}
          />
        </Modal.Body>
        <Modal.Footer>
          <Row className={'container-buttons'}>
            <Button
              color={'secondary'}
              onClick={() => setChildrenToSort(sortChildrenDefault)}>
              Cerrar
            </Button>
            <Button
              color={'accent'}
              onClick={() => {
                updateManuallySortedCategories(childrenToSort.children)
                setChildrenToSort(sortChildrenDefault)
              }}>
              Continuar
            </Button>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
